import React, { useState, useEffect, useMemo, useCallback } from "react";
import { auth, db, storage } from "../firebase-config";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import QRCodeDownload from "./QRCodeDownload.tsx";
import { FileUploader } from "./FileUploader";

// Event Card Component - defined outside to prevent recreation
const EventCard = ({ event, onEdit, onDelete }) => (
  <div className="bg-white border rounded-lg p-4 mb-3">
    <div className="flex justify-between items-start mb-2">
      <div>
        <h4 className="font-semibold text-lg">{event.name}</h4>
        <p className="text-sm text-gray-600">
          {event.date} • {event.startTime} - {event.endTime}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(event)}
          className="text-blue-500 hover:text-blue-700 px-2 py-1"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(event.id)}
          className="text-red-500 hover:text-red-700 px-2 py-1"
        >
          Delete
        </button>
      </div>
    </div>
    {event.description && (
      <p className="text-gray-700 mb-2">{event.description}</p>
    )}
    {event.posterUrls && event.posterUrls.length > 0 && (
      <div className="flex gap-2 mt-2">
        {event.posterUrls.slice(0, 3).map((url, i) => (
          <img
            key={i}
            src={url}
            alt={`Event poster ${i + 1}`}
            className="w-20 h-20 object-cover rounded"
          />
        ))}
        {event.posterUrls.length > 3 && (
          <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center text-sm text-gray-600">
            +{event.posterUrls.length - 3}
          </div>
        )}
      </div>
    )}
  </div>
);

const Settings = () => {
  const [defaultPlaylist, setDefaultPlaylist] = useState("");
  const [accountName, setAccountName] = useState("");
  const [crossfadeDuration, setCrossfadeDuration] = useState("");
  const [fileName, setFileName] = useState("");
  const [userRequestRate, setUserRequestRate] = useState(0);
  const [songRate, setSongRate] = useState(0);
  const [moodButtonDailyLimit, setMoodButtonDailyLimit] = useState(1);
  // Tab state for navigation
  const [activeTab, setActiveTab] = useState("basic");

  // Venue information state
  const [venueInfo, setVenueInfo] = useState({
    name: "",
    description: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    email: "",
    website: "",
    hours: {
      monday: { open: "", close: "", closed: false },
      tuesday: { open: "", close: "", closed: false },
      wednesday: { open: "", close: "", closed: false },
      thursday: { open: "", close: "", closed: false },
      friday: { open: "", close: "", closed: false },
      saturday: { open: "", close: "", closed: false },
      sunday: { open: "", close: "", closed: false },
    },
    socialMedia: {
      facebook: "",
      instagram: "",
      twitter: "",
    },
    bannerImage: "",
  });

  // Events state
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventFormData, setEventFormData] = useState({
    name: "",
    date: "",
    startTime: "",
    endTime: "",
    description: "",
    posterUrls: [],
    ticketPrice: "",
    ticketLink: "",
    playlist: "",
    status: "upcoming",
  });

  // Loading states
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [avatarImage, setAvatarImage] = useState("");
  // progress
  const [percent, setPercent] = useState(0);

  // Banner image upload progress
  const [bannerPercent, setBannerPercent] = useState(0);

  const [playlists, setPlaylists] = useState([]);

  const handleFile = (file) => {
    setFileName(file.name);
    if (!file) {
      alert("Please upload an image first!");
    }

    const storageRef = ref(
      storage,
      `muuzbox/channels/${auth.currentUser.uid}/${file.name}`
    );

    // progress can be paused and resumed. It also exposes progress updates.
    // Receives the storage reference and the file to upload.
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const percent = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );

        // update progress
        setPercent(percent);
      },
      (err) => console.log(err),
      () => {
        // download url
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          console.log(url);
          // put this url in the clients object for this user
          // then display it
          setAvatarImage(url);
        });
      }
    );
  };

  // load up settings from users table settings object fields with all settings for muuzbox: muuzboxsettings

  useEffect(() => {
    const settings = async () => {
      const docRef = doc(db, "users", auth.currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setAccountName(docSnap.data().muuzboxAccountName.toLowerCase());
        setCrossfadeDuration(docSnap.data().muuzboxCrossFadeDuration);
        setMoodButtonDailyLimit(Number(docSnap.data().muuzboxMoodButtonDailyLimit) || 1);
      }
    };
    settings();
  }, []);

  useEffect(() => {
    const playlistsRef = collection(
      db,
      "users",
      auth.currentUser.uid,
      "playlists"
    );
    const queryPlaylists = query(playlistsRef, orderBy("createdAt"));
    const unsubscribe = onSnapshot(queryPlaylists, (snapshot) => {
      const playlists = [];
      snapshot.forEach((doc) => {
        playlists.push(doc.data());
      });
      setPlaylists(playlists);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const getChannel = async () => {
      if (accountName) {
        const docRef2 = doc(db, "clients", accountName);
        const docSnap2 = await getDoc(docRef2);
        if (docSnap2.exists()) {
          const data = docSnap2.data();
          setDefaultPlaylist(data.defaultPlaylist || "");
          setAvatarImage(data.avatarImage || "");

          // Load venue info if exists
          if (data.venueInfo) {
            setVenueInfo((prev) => ({ ...prev, ...data.venueInfo }));
          }

          // Load events if exists
          if (data.events) {
            setEvents(data.events);
          }
        }
      }
    };
    getChannel();
  }, [accountName]);

  const handleSettings = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const settingObj = {
        muuzboxAccountName: accountName,
        muuzboxCrossFadeDuration: crossfadeDuration,
        muuzboxUserRequestRate: userRequestRate,
        muuzboxSongRate: songRate,
        muuzboxMoodButtonDailyLimit: Number(moodButtonDailyLimit) || 1,
      };
      console.log('Saving settings with moodButtonDailyLimit:', Number(moodButtonDailyLimit) || 1);
      await updateDoc(doc(db, "users", auth.currentUser.uid), settingObj);

      // Save all client data including venue info and events
      await setDoc(
        doc(db, "clients", accountName),
        {
          clientId: auth.currentUser.uid,
          defaultPlaylist: defaultPlaylist,
          avatarImage: avatarImage,
          venueInfo: venueInfo,
          events: events,
          moodButtonDailyLimit: Number(moodButtonDailyLimit) || 1,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      alert("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Error saving settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const muuzboxURL = (hash) => {
    if (hash) {
      return "https://muuzbox.com/#" + hash.replace(/\s/g, "");
    } else {
      return "https://muuzbox.com/#ctfomusic";
    }
  };


  // const upload = () => {
  //   if (image == null) return;
  //   storage
  //     .ref(`/images/${image.name}`)
  //     .put(image)
  //     .on("state_changed", alert("success"), alert);
  // };

  // Handle banner image upload
  const handleBannerUpload = (file) => {
    if (!file) return;

    const storageRef = ref(
      storage,
      `muuzbox/venues/${auth.currentUser.uid}/banner/${file.name}`
    );

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const percent = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setBannerPercent(percent);
      },
      (err) => console.log(err),
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          setVenueInfo((prev) => ({ ...prev, bannerImage: url }));
          setBannerPercent(0);
        });
      }
    );
  };

  // Handle event poster upload
  const handleEventPosterUpload = async (files, isEditing = false) => {
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const uploadPromises = [];

    for (const file of files) {
      const storageRef = ref(
        storage,
        `muuzbox/events/${auth.currentUser.uid}/${Date.now()}_${file.name}`
      );

      const uploadTask = uploadBytesResumable(storageRef, file);

      const uploadPromise = new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          () => {},
          (error) => reject(error),
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      uploadPromises.push(uploadPromise);
    }

    try {
      const urls = await Promise.all(uploadPromises);
      if (isEditing) {
        setEventFormData((prev) => ({
          ...prev,
          posterUrls: [...prev.posterUrls, ...urls],
        }));
      } else {
        setEventFormData((prev) => ({
          ...prev,
          posterUrls: urls,
        }));
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("Error uploading images");
    } finally {
      setUploadingImages(false);
    }
  };

  // Event management functions
  const handleAddEvent = () => {
    setShowEventForm(true);
    setEditingEvent(null);
    setEventFormData({
      name: "",
      date: "",
      startTime: "",
      endTime: "",
      description: "",
      posterUrls: [],
      ticketPrice: "",
      ticketLink: "",
      playlist: "",
      status: "upcoming",
    });
  };

  const handleEditEvent = (event) => {
    setShowEventForm(true);
    setEditingEvent(event.id);
    setEventFormData(event);
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      setEvents(events.filter((e) => e.id !== eventId));
    }
  };

  const handleSaveEvent = () => {
    const eventToSave = {
      ...eventFormData,
      id: editingEvent || Date.now().toString(),
    };

    if (editingEvent) {
      setEvents(events.map((e) => (e.id === editingEvent ? eventToSave : e)));
    } else {
      setEvents([...events, eventToSave]);
    }

    setShowEventForm(false);
    setEditingEvent(null);
  };

  const handleRemoveEventPoster = (index) => {
    setEventFormData((prev) => ({
      ...prev,
      posterUrls: prev.posterUrls.filter((_, i) => i !== index),
    }));
  };

  // const FileUploader = ({ handleFile }) => {
  //   // Create a reference to the hidden file input element
  //   const hiddenFileInput = useRef(null);

  //   // Programatically click the hidden file input element
  //   // when the Button component is clicked
  //   const handleClick = (event) => {
  //     console.log('handleClick')
  //     hiddenFileInput.current.click();
  //   };
  //   // Call a function (passed as a prop from the parent component)
  //   // to handle the user-selected file
  //   const handleChange = (event) => {
  //     const fileUploaded = event.target.files[0];
  //     handleFile(fileUploaded);
  //   };
  //   return (
  //     <>
  //       <button className="button-upload" onClick={handleClick}>
  //         Upload a file
  //       </button>
  //       <input
  //         type="file"
  //         onChange={handleChange}
  //         ref={hiddenFileInput}
  //         style={{ display: "none" }} // Make the file input element invisible
  //       />
  //     </>
  //   );
  // };

  // Tab component
  // const TabButtonx = ({ tabName, label, icon }) => (
  //   <button
  //     // onClick={() => {console.log('tabName', tabName); setActiveTab(tabName)}}
  //     onClick={() => {
  //       // setShowUserModal(false);
  //       // setActiveTab(tabName)
  //       console.log("tabName", tabName);
  //     }}
  //     className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
  //       activeTab === tabName
  //         ? "bg-indigo-500 text-white"
  //         : "bg-gray-200 text-gray-700 hover:bg-gray-300"
  //     }`}
  //   >
  //     <span className="mr-2">{icon}</span>
  //     {label}
  //   </button>
  // );

  const TabButton = useMemo(
    () =>
      ({ tabName, label, icon }) =>
        (
          <button
            // eslint-disable-next-line react-hooks/rules-of-hooks
            onClick={useCallback(() => {
              setActiveTab(tabName);
            }, [tabName])}
            className={`px-4 py-2 nodecor font-semibold rounded-t-lg transition-colors ${
              activeTab === tabName
                ? "bg-indigo-500 text-yellow-500"
                : "bg-gray-200 text-white hover:bg-yellow-500"
            }`}
          >
            <span className="mr-2">{icon}</span>
            {label}
          </button>
        ),
    [activeTab]
  );

  return (
    <div className="text-black">
      {/* Tab Navigation */}
      <div className="flex justify-between items-center gap-2 mb-4 border-b">
        <div className="flex gap-2">
          <TabButton tabName="basic" label="Basic Settings" icon="⚙️" />
          <TabButton tabName="venue" label="Venue Info" icon="🏢" />
          <TabButton tabName="events" label="Events" icon="🎉" />
          <TabButton tabName="homepage" label="Homepage" icon="🏠" />
        </div>
        
        {/* Save Button */}
        <button
          onClick={(event) => handleSettings(event)}
          disabled={saving}
          className="flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-400 rounded-xl text-white px-4 py-2"
        >
          <span className="ml-0">
            <svg
              className="w-6 h-6 -mt-px text-white mr-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6C4 4.89543 4.89543 4 6 4H12H14.1716C14.702 4 15.2107 4.21071 15.5858 4.58579L19.4142 8.41421C19.7893 8.78929 20 9.29799 20 9.82843V12V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
                stroke="#ffffff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M8 4H13V7C13 7.55228 12.5523 8 12 8H9C8.44772 8 8 7.55228 8 7V4Z"
                stroke="#ffffff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 15C7 13.8954 7.89543 13 9 13H15C16.1046 13 17 13.8954 17 15V20H7V15Z"
                stroke="#ffffff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span className="text-sm font-black">
            {saving ? "SAVING..." : "SAVE ALL SETTINGS"}
          </span>
        </button>

        {/* <button
          // onClick={() => {console.log('tabName', tabName); setActiveTab(tabName)}}
          onClick={() => {
            setActiveTab('basic')
            console.log("tabName basic");
          }}
          className={`px-4 py-2 font-semibold rounded-t-lg transition-colors`}
        >
          basic
        </button>

        <button
          onClick={() => {
            setActiveTab('venue')
            console.log("tabName venue");
          }}
          className={`px-4 py-2 font-semibold rounded-t-lg transition-colors`}
        >
          venue
        </button>

        <button
          onClick={() => {
            setActiveTab('events')
            console.log("tabName events");
          }}
          className={`px-4 py-2 font-semibold rounded-t-lg transition-colors`}
        >
          events
        </button>

        <button
          onClick={() => {
            setActiveTab('homepage')
            console.log("tabName homepage");
          }}
          className={`px-4 py-2 font-semibold rounded-t-lg transition-colors`}
        >
          homepage
        </button> */}
      </div>

      <div className="overflow-auto h-[550px]">
        {/* Basic Settings Tab */}
        {activeTab === "basic" && (
          <div className="p-4 space-y-6">
            {/* Channel Configuration Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Channel Configuration</h3>
              
              <div className="space-y-4">
                {/* Channel Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Channel Name
                  </label>
                  <input
                    type="text"
                    value={accountName}
                    disabled={true}
                    onChange={(event) =>
                      setAccountName(event.target.value.toLowerCase())
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter your channel name"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Your channel URL: {muuzboxURL(accountName)}
                  </p>
                </div>

                {/* Default Playlist */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Playlist
                  </label>
                  <select
                    value={defaultPlaylist}
                    onChange={(e) => setDefaultPlaylist(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select a playlist</option>
                    {playlists.map((item, i) => (
                      <option key={i} value={item.playlistName}>
                        {item.playlistName}
                      </option>
                    ))}
                  </select>
                  {defaultPlaylist && (
                    <p className="mt-1 text-sm text-gray-500">
                      Currently selected: <strong>{defaultPlaylist}</strong>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Channel Branding Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Channel Branding</h3>
              
              <div className="flex flex-col items-center space-y-4">
                {avatarImage && (
                  <img 
                    src={avatarImage} 
                    alt="Channel avatar" 
                    className="w-48 h-48 rounded-lg object-cover border border-gray-200"
                  />
                )}
                <div className="text-center">
                  <FileUploader handleFile={handleFile} />
                  {fileName && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {fileName}
                    </p>
                  )}
                  {percent > 0 && percent < 100 && (
                    <div className="mt-2">
                      <div className="w-48 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{percent}% uploaded</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Audio Settings Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Audio Settings</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Crossfade Duration (seconds)
                </label>
                <input
                  type="number"
                  min="5"
                  max="20"
                  value={crossfadeDuration}
                  onChange={(event) => setCrossfadeDuration(event.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Duration of crossfade between songs (5-20 seconds)
                </p>
              </div>
            </div>

            {/* Rate Limiting Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Rate Limiting</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User Request Rate
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={userRequestRate}
                      onChange={(event) => setUserRequestRate(event.target.value)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="text-sm text-gray-600">per hour</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Maximum requests a user can make per hour
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Song Play Rate
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={songRate}
                      onChange={(event) => setSongRate(event.target.value)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="text-sm text-gray-600">per hour</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Maximum times a song can be played per hour
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mood Button Daily Limit
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={moodButtonDailyLimit}
                      onChange={(event) => setMoodButtonDailyLimit(parseInt(event.target.value) || 1)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="text-sm text-gray-600">per day</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Maximum times a user can click any mood button per day
                  </p>
                </div>
              </div>
            </div>

            {/* QR Code Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">QR Code</h3>
              
              <div className="flex flex-col items-center">
                <p className="text-sm text-gray-600 mb-4">
                  Share your channel with this QR code
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <QRCodeDownload url={muuzboxURL(accountName)} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Venue Info Tab */}
        {activeTab === "venue" && (
          <div className="p-4">
            <h3 className="text-xl font-semibold mb-4">Venue Information</h3>

            {/* Venue Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Venue Name
                </label>
                <input
                  type="text"
                  value={venueInfo.name}
                  onChange={(e) =>
                    setVenueInfo({ ...venueInfo, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter venue name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  value={venueInfo.description}
                  onChange={(e) =>
                    setVenueInfo({ ...venueInfo, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Describe your venue..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={venueInfo.phone}
                    onChange={(e) =>
                      setVenueInfo({ ...venueInfo, phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={venueInfo.email}
                    onChange={(e) =>
                      setVenueInfo({ ...venueInfo, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="venue@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={venueInfo.website}
                  onChange={(e) =>
                    setVenueInfo({ ...venueInfo, website: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://www.yourvenue.com"
                />
              </div>

              <hr className="my-4" />

              <h4 className="font-semibold">Location</h4>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={venueInfo.address}
                  onChange={(e) =>
                    setVenueInfo({ ...venueInfo, address: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="123 Main St"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    type="text"
                    value={venueInfo.city}
                    onChange={(e) =>
                      setVenueInfo({ ...venueInfo, city: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={venueInfo.state}
                    onChange={(e) =>
                      setVenueInfo({ ...venueInfo, state: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="NY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">ZIP</label>
                  <input
                    type="text"
                    value={venueInfo.zipCode}
                    onChange={(e) =>
                      setVenueInfo({ ...venueInfo, zipCode: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="10001"
                  />
                </div>
              </div>

              <hr className="my-4" />

              <h4 className="font-semibold">Social Media</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Facebook
                  </label>
                  <input
                    type="text"
                    value={venueInfo.socialMedia.facebook}
                    onChange={(e) =>
                      setVenueInfo({
                        ...venueInfo,
                        socialMedia: {
                          ...venueInfo.socialMedia,
                          facebook: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="facebook.com/yourvenue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Instagram
                  </label>
                  <input
                    type="text"
                    value={venueInfo.socialMedia.instagram}
                    onChange={(e) =>
                      setVenueInfo({
                        ...venueInfo,
                        socialMedia: {
                          ...venueInfo.socialMedia,
                          instagram: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="@yourvenue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Twitter
                  </label>
                  <input
                    type="text"
                    value={venueInfo.socialMedia.twitter}
                    onChange={(e) =>
                      setVenueInfo({
                        ...venueInfo,
                        socialMedia: {
                          ...venueInfo.socialMedia,
                          twitter: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="@yourvenue"
                  />
                </div>
              </div>

              <hr className="my-4" />

              <h4 className="font-semibold">Banner Image</h4>
              <div className="space-y-2">
                {venueInfo.bannerImage && (
                  <img
                    src={venueInfo.bannerImage}
                    alt="Venue banner"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleBannerUpload(e.target.files[0])}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                />
                {bannerPercent > 0 && (
                  <p className="text-sm">{bannerPercent}% uploaded</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === "events" && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Events</h3>
              <button
                onClick={handleAddEvent}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
              >
                + Add Event
              </button>
            </div>

            {/* Event Form */}
            {showEventForm && (
              <div className="bg-gray-50 border rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-3">
                  {editingEvent ? "Edit Event" : "New Event"}
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Event Name
                    </label>
                    <input
                      type="text"
                      value={eventFormData.name}
                      onChange={(e) =>
                        setEventFormData({
                          ...eventFormData,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Summer Music Festival"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={eventFormData.date}
                        onChange={(e) =>
                          setEventFormData({
                            ...eventFormData,
                            date: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={eventFormData.startTime}
                        onChange={(e) =>
                          setEventFormData({
                            ...eventFormData,
                            startTime: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={eventFormData.endTime}
                        onChange={(e) =>
                          setEventFormData({
                            ...eventFormData,
                            endTime: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <textarea
                      value={eventFormData.description}
                      onChange={(e) =>
                        setEventFormData({
                          ...eventFormData,
                          description: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={3}
                      placeholder="Describe your event..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Ticket Price
                      </label>
                      <input
                        type="number"
                        value={eventFormData.ticketPrice}
                        onChange={(e) =>
                          setEventFormData({
                            ...eventFormData,
                            ticketPrice: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="25.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Ticket Link
                      </label>
                      <input
                        type="url"
                        value={eventFormData.ticketLink}
                        onChange={(e) =>
                          setEventFormData({
                            ...eventFormData,
                            ticketLink: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="https://tickets.com/event"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Event Playlist
                    </label>
                    {/* <select
                      value={eventFormData.playlist}
                      onChange={(e) => setEventFormData({...eventFormData, playlist: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select playlist</option>
                      {playlists.map((pl, i) => (
                        <option key={i} value={pl.playlistName}>{pl.playlistName}</option>
                      ))}
                    </select> */}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Event Posters
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) =>
                        handleEventPosterUpload(
                          Array.from(e.target.files),
                          !!editingEvent
                        )
                      }
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                    />
                    {uploadingImages && (
                      <p className="text-sm mt-1">Uploading images...</p>
                    )}

                    {/* Show uploaded posters */}
                    {eventFormData.posterUrls.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {eventFormData.posterUrls.map((url, index) => (
                          <div key={index} className="relative">
                            <img
                              src={url}
                              alt={`Poster ${index + 1}`}
                              className="w-24 h-24 object-cover rounded"
                            />
                            <button
                              onClick={() => handleRemoveEventPoster(index)}
                              className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowEventForm(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEvent}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
                    >
                      {editingEvent ? "Update" : "Create"} Event
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Events List */}
            <div className="space-y-3">
              {events.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No events yet. Click "Add Event" to create one.
                </p>
              ) : (
                events.map((event) => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    onEdit={handleEditEvent}
                    onDelete={handleDeleteEvent}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Homepage Preview Tab */}
        {activeTab === "homepage" && (
          <div className="p-4">
            <h3 className="text-xl font-semibold mb-4">Homepage Preview</h3>

            <div className="bg-gray-100 rounded-lg p-4">
              {/* Banner */}
              {venueInfo.bannerImage && (
                <img
                  src={venueInfo.bannerImage}
                  alt="Venue banner"
                  className="w-full h-48 object-contain rounded-lg mb-4"
                />
              )}

              {/* Venue Info */}
              <div className="bg-white rounded-lg p-4 mb-4">
                <div className="flex items-start gap-4">
                  {avatarImage && (
                    <img
                      src={avatarImage}
                      alt="Venue avatar"
                      className="w-24 h-24 rounded-full object-contain"
                    />
                  )}
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold">
                      {venueInfo.name || accountName}
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {venueInfo.description}
                    </p>

                    {venueInfo.address && (
                      <p className="text-sm text-gray-500 mt-2">
                        📍 {venueInfo.address}, {venueInfo.city},{" "}
                        {venueInfo.state} {venueInfo.zipCode}
                      </p>
                    )}

                    <div className="flex gap-4 mt-3">
                      {venueInfo.phone && (
                        <span className="text-sm">📞 {venueInfo.phone}</span>
                      )}
                      {venueInfo.email && (
                        <span className="text-sm">✉️ {venueInfo.email}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Upcoming Events */}
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold mb-3">Upcoming Events</h3>
                {events.filter((e) => e.status === "upcoming").length === 0 ? (
                  <p className="text-gray-500">No upcoming events</p>
                ) : (
                  <div className="space-y-2">
                    {events
                      .filter((e) => e.status === "upcoming")
                      .map((event) => (
                        <div key={event.id} className="border rounded p-3">
                          <h4 className="font-semibold">{event.name}</h4>
                          <p className="text-sm text-gray-600">
                            {event.date} • {event.startTime} - {event.endTime}
                          </p>
                          {event.posterUrls && event.posterUrls[0] && (
                            <img
                              src={event.posterUrls[0]}
                              alt="Event poster"
                              className="w-full h-32 object-contain rounded mt-2"
                            />
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Settings;
